// Client-seitiges Filtern der Interessenten-Listen nach den aktiven Kurs-Filtern.
// Der öffentliche Endpoint interestListsPublic nimmt keine Filter-Argumente an,
// deshalb spiegeln wir hier die für Interessenten-Listen sinnvollen Filter
// (Kursart, Kategorie, Standort, Wochentag) nach, damit sie sich wie die Kursliste verhalten.

export type InterestListFilterCriteria = {
    courseTypeId?: string | null
    courseTypeIds?: ReadonlyArray<string> | null
    // Kategorie der Kursart; courseCategoryId und courseCategoryIds wie bei der Kursart als Vereinigung.
    courseCategoryId?: string | null
    courseCategoryIds?: ReadonlyArray<string> | null
    // Standort-Filter: Der EUA-Ortsfilter (CityFilter) setzt einen city-String und
    // nullt locationId; der Location-Filter setzt eine locationId. Beide werden
    // unterstützt, damit "Ort stimmt überein" die Zeile behält.
    locationId?: string | null
    city?: string | null
    // Wochentage (DayFilter-Werte, z.B. "Montag"). Eine Liste passt, wenn sie
    // mindestens ein Zeitfenster an einem der gewählten Tage hat.
    dayFilter?: ReadonlyArray<string> | null
}

export type InterestListLike = {
    courseType: { id: string; category?: { id: string } | null }
    location: { id: string; city?: string | null }
    timeSlots?: ReadonlyArray<{ weekday: string }> | null
}

/**
 * Behält eine Liste, wenn sie ALLE gesetzten Filter erfüllt (AND). Nicht
 * gesetzte Filter schränken nicht ein. courseTypeId und courseTypeIds werden
 * als Vereinigung behandelt (eine Liste passt, wenn ihr Kursart-Id in einem
 * der beiden vorkommt).
 */
export function matchesInterestListFilter(list: InterestListLike, criteria: InterestListFilterCriteria): boolean {
    const wantedCourseTypeIds = new Set<string>()
    if (criteria.courseTypeId) wantedCourseTypeIds.add(criteria.courseTypeId)
    ;(criteria.courseTypeIds ?? []).forEach((id) => {
        if (id) wantedCourseTypeIds.add(id)
    })

    if (wantedCourseTypeIds.size > 0 && !wantedCourseTypeIds.has(list.courseType.id)) {
        return false
    }

    const wantedCategoryIds = [criteria.courseCategoryId, ...(criteria.courseCategoryIds ?? [])].filter(Boolean)
    if (wantedCategoryIds.length > 0 && !wantedCategoryIds.includes(list.courseType.category?.id)) {
        return false
    }

    if (criteria.locationId && list.location.id !== criteria.locationId) {
        return false
    }

    if (criteria.city && list.location.city !== criteria.city) {
        return false
    }

    const wantedWeekdays = criteria.dayFilter ?? []
    if (wantedWeekdays.length > 0) {
        const slotWeekdays = new Set((list.timeSlots ?? []).map((slot) => slot.weekday))
        if (!wantedWeekdays.some((weekday) => slotWeekdays.has(weekday))) {
            return false
        }
    }

    return true
}

export function filterInterestLists<T extends InterestListLike>(lists: ReadonlyArray<T>, criteria: InterestListFilterCriteria): Array<T> {
    return lists.filter((list) => matchesInterestListFilter(list, criteria))
}
